"use client";

import { misc, util } from "@/common/theme";
import CustomSplitText from "@/mono/components/CustomSplitText";
import { TransitionLink } from "@/mono/components/TransitionLink";
import useAnimation from "@/mono/lib/hooks/useAnimation";
import { useIsClient } from "@/mono/lib/hooks/useIsClient";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import { map } from "@/mono/lib/math";
import {
  EASE_CUSTOM_2,
  easeInExpo,
  easeInOutExpo,
  easeOutCirc,
} from "@/mono/lib/styles/easing";
import SplitText from "mono/components/SplitText";
import Link from "next/link";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import styled, { css, useTheme } from "styled-components";

export const iconSize = "120rem";

const BorderedIcon = forwardRef(
  (
    {
      icon = "arrow",
      onClick,
      className = "",
      href,
      text,

      transparent,
      animateOnScroll = true,
      dontTriggerPageTransition = false,
      offset = 0,
      onScrollParams = {},
      size,
      disableSplitText = false,
      textColor,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const isClient = useIsClient();

    const wrapper = useRef();
    const isLink = !(href === undefined || href === "" || href === null);
    const theme = useTheme();

    const textRef = useRef();

    const onUpdate = (v) => {
      const rotation = map(v, 0, 1, -50, 0);
      const scale = v;
      const opacity = v;
      if (wrapper.current)
        wrapper.current.style.transform = `scale(${scale}) rotateZ(${rotation}deg)`;
      if (wrapper.current) wrapper.current.style.opacity = opacity;
    };

    const { animateIn, animateOut } = useAnimation({
      inParams: {
        duration: 1.4,
        delay: 0.4,
        ease: EASE_CUSTOM_2,
        onUpdate,
      },
      outParams: {
        duration: 1,
        ease: EASE_CUSTOM_2,
        onUpdate,
      },
    });

    useImperativeHandle(ref, () => ({
      animateIn: (params) => {
        animateIn(params);

        if (!animateOnScroll && text) {
          const delay = params.delay ?? 0;
          if (textRef.current)
            textRef.current?.animateIn({ delay: delay + 0.4 });
        }
      },
      animateOut,
    }));

    useScrollRigScrollTrigger({
      el: wrapper,
      offset,
      handleIn: () => {
        if (!animateOnScroll) return;
        animateIn(onScrollParams);
      },
    });

    const ArrowIcon = useMemo(() => {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000">
          <path
            fill={theme.theme.textColor}
            d="M867.9 500 847 479.1 557.5 189.6l-20.9 21 274.6 274.6H142.5v29.6h668.8L536.6 789.4l20.9 21L847 520.9l20.9-20.9z"
          />
        </svg>
      );
    }, [theme]);

    const ChevronLeft = useMemo(() => {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 113 216"
        >
          <path
            fill={theme.theme.textColor}
            d="M110.2 105.2 4.90002 0 0 4.90002 102.9 107.8 0 210.5l4.90002 4.9L112.8 107.8l-2.6-2.6Z"
          />
        </svg>
      );
    }, [theme]);

    const ChevronRight = useMemo(() => {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 113 216"
        >
          <path
            fill={theme.theme.textColor}
            d="M110.2 105.2 4.90002 0 0 4.90002 102.9 107.8 0 210.5l4.90002 4.9L112.8 107.8l-2.6-2.6Z"
          />
        </svg>
      );
    }, [theme]);

    const CloseIcon = useMemo(() => {
      const strokeWidth = 26;
      return (
        <svg
          width="406"
          height="406"
          viewBox="0 0 406 406"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 403.001L403 3.21094"
            stroke={theme.theme.textColor}
            strokeWidth={strokeWidth}
            strokeMiterlimit="10"
          />
          <path
            d="M3 3L403 402.79"
            stroke={theme.theme.textColor}
            strokeWidth={strokeWidth}
            strokeMiterlimit="10"
          />
        </svg>
      );
    }, [theme]);

    const iconMap = useMemo(() => {
      return {
        arrow: ArrowIcon,
        close: CloseIcon,
        chevronLeft: ChevronLeft,
        chevronRight: ChevronRight,
      };
    }, []);

    const iconComponent = useMemo(() => {
      return (
        <Container
          className={`icon-container ${className}`}
          $disabled={disabled}
          $textColor={textColor}
        >
          <Wrapper
            $disabled={disabled}
            ref={wrapper}
            className={`icon-wrapper icon-${icon}`}
            onClick={onClick}
            $transparent={transparent}
            $size={size}
            {...props}
          >
            <div className="icon icon-first">{iconMap[icon]}</div>
            <div className="icon icon-duplicate">{iconMap[icon]}</div>
            <div className="icon-background"></div>
          </Wrapper>
          {text &&
            (disableSplitText ? (
              <h4>{text}</h4>
            ) : (
              <SplitText
                ref={textRef}
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
                animateOnScroll={animateOnScroll}
                tag="h4"
              />
            ))}
        </Container>
      );
    }, [
      theme,
      className,
      icon,
      onClick,
      transparent,
      disabled,
      size,
      text,
      animateOnScroll,
      disableSplitText,
      props,
      iconMap,
    ]);

    if (!isClient) return null;

    if (dontTriggerPageTransition && isLink)
      return <Link href={href ?? "#"}>{iconComponent}</Link>;

    return isLink ? (
      <TransitionLink href={href ?? "#"}>{iconComponent}</TransitionLink>
    ) : (
      iconComponent
    );
  }
);

export default BorderedIcon;

const iconHover = css`
  .icon-first {
    transform: translateX(var(--translation));
  }

  .icon-duplicate {
    transform: translateX(0%) !important;

    path {
      fill: ${({ theme }) => theme.theme.background};
      stroke: ${({ theme }) => theme.theme.background};
    }
  }

  .icon-background {
    transform: scale(1.5);
    transition-delay: 0s;
  }
`;

const Container = styled.div`
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    ${({ $disabled }) =>
      !$disabled &&
      css`
        h4 {
          ${util.iconUnderlineHoverTransition}
        }
        ${iconHover}
      `}
  }

  h4 {
    position: relative;
    margin-left: 50rem;
    color: ${({ $textColor, theme }) => $textColor || theme.theme.textColor};
    ${util.iconUnderlineHover()}
  }
`;

const Wrapper = styled.div`
  --translation: 150%;
  --duration: 0.8s;

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      *,
      & {
        pointer-events: none !important;
      }
    `}

  border: 1px solid ${({ theme }) => theme.theme.textColor};
  border-radius: ${misc.borderRadius};

  width: ${({ $size }) => $size || iconSize};
  height: ${({ $size }) => $size || iconSize};

  .icon-first {
    path {
      fill: ${({ theme }) => theme.theme.textColor};
      stroke: ${({ theme }) => theme.theme.textColor};
    }
  }

  .icon-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.theme.textColor};
    border-radius: 50%;
    transform: scale(0);
    transition: transform var(--duration) ${easeInOutExpo};
    transition-delay: 0.1s;
    z-index: 0;
    /* transform-origin: center center; */
  }

  transform: scale(0) rotate(180deg);
  transform-origin: center;
  opacity: 0;
  overflow: hidden;

  .icon {
    width: ${({ $size }) => $size || iconSize};
    height: ${({ $size }) => $size || iconSize};

    display: flex;
    justify-content: center;
    align-items: center;

    transition: transform var(--duration) ${easeInOutExpo};
    z-index: 1;

    svg {
      width: 75%;
      height: 75%;

      path {
        transition: fill var(--duration) ${easeInOutExpo};
      }
    }

    &.active {
      transform: translateX(0%);
    }

    &.icon-duplicate {
      transform: translateX(calc(var(--translation) * -1));
      position: absolute;
      top: 0;
      left: 0;
      transition: transform calc(var(--duration) * 0.85) ${easeOutCirc}
        calc(var(--duration) * 0.35);

      path {
        fill: ${({ theme }) => theme.theme.background};
        stroke: ${({ theme }) => theme.theme.background};
      }
    }
  }

  &.icon-close {
    svg {
      width: 50%;
      height: 50%;
    }
  }

  &.icon-chevronRight,
  &.icon-chevronLeft {
    svg {
      width: 70%;
      height: 50%;
    }
  }

  &.icon-chevronLeft {
    svg {
      transform: scaleX(-1);
    }
  }

  &:hover {
    ${({ $disabled }) => !$disabled && iconHover}
  }
`;
